/*
  Härtegrad-Rechner für Standardbetten (Luxusbetten24)

  Standardbetten haben einen Härtegrad fürs ganze Bett (keine Wahl links/rechts). Der Rechner fragt deshalb zuerst
  „Allein“ oder „Zu zweit“; zu zweit wird aus beiden Profilen ein gemeinsamer Härtegrad (z. B. H4 + H2 → H3).

  Einstieg: Link „Welcher passt zu mir?“ am Härtegrad (snippets/cx-haertegrad-rechner.liquid, gerendert aus
  snippets/variant-picker.liquid). „Übernehmen“ wählt den passenden Wert in der echten Variantenauswahl (Wert beginnt mit
  H2/H3/H4) und löst deren change-Ereignis aus – das Theme aktualisiert dann Variante, Preis und Lieferzeit selbst.
  Ist der empfohlene Grad gerade nicht wählbar (ausgeblendet/ausverkauft, z. B. im Lager-Modus), nimmt der Rechner den
  nächstliegenden und sagt das.

  Rechenlogik: score(), evaluate(), combined() und gaugePct() sind 1:1 aus der Härtegrad-Analyse des Konfigurators
  übernommen (snippets/main-customized-bed__hardness-finder.liquid, window.cxHardnessFinderData). Angepasst ist nur
  combined(): Texte „ein Härtegrad fürs ganze Bett“ statt „eine Matratze“, und zwei gleiche Grade bleiben dieser Grad
  (im Konfigurator kann der Stützkraft-Aufschlag zweimal H3 zu H4 machen). Wer dort an der Rechnung etwas ändert,
  zieht es hier nach, damit Produktseite und Konfigurator dieselbe Empfehlung geben.
  „Morgens verspannt?“ fragt der Rechner hier nicht ab (pain bleibt 'none'), damit der Dialog kurz bleibt.
*/

// Gleiche Namen wie an den Härtegrad-Werten der Produktseite (snippets/cx-haertegrad-label.liquid: „H3 (Mittel)“)
const GRADE_NAMES = { H1: 'Sehr weich', H2: 'Weich', H3: 'Mittel', H4: 'Fest', H5: 'Sehr fest' };
const POSITIONS = [
  { key: 'side', label: 'Seite', hint: 'Schulter & Hüfte sinken ein' },
  { key: 'back', label: 'Rücken', hint: 'gleichmäßige Stütze' },
  { key: 'stomach', label: 'Bauch', hint: 'Becken darf nicht einsinken' },
  { key: 'mixed', label: 'Wechselnd', hint: 'mal so, mal so' },
];
const FEELS = [
  { key: 'soft', label: 'Weicher', hint: 'einsinken' },
  { key: 'balanced', label: 'Ausgewogen', hint: 'die Mitte' },
  { key: 'firm', label: 'Fester', hint: 'liegen wie ein Brett' },
];

// ---- Rechenlogik aus dem Konfigurator (unverändert) ----------------------------------------------------------------

// Kontinuierlicher Score 2.0–4.4 → H2 (<2.5) / H3 (<3.5) / H4
function score(p) {
  const h = p.height / 100;
  const w = p.weight;
  const bmi = w / (h * h);
  let s;
  if (w <= 55) s = 2.0;
  else if (w <= 70) s = 2.0 + (w - 55) / 15 * 0.6;
  else if (w <= 85) s = 2.6 + (w - 70) / 15 * 0.5;
  else if (w <= 100) s = 3.1 + (w - 85) / 15 * 0.5;
  else if (w <= 120) s = 3.6 + (w - 100) / 20 * 0.5;
  else s = 4.1 + Math.min((w - 120) / 30, 1) * 0.3;

  if (bmi >= 30) s += 0.2;
  else if (bmi >= 27) s += 0.1;
  else if (bmi < 19) s -= 0.15;

  if (p.position === 'side') s -= 0.3;
  else if (p.position === 'stomach') s += 0.3;
  else if (p.position === 'back') s += 0.05;

  if (p.feel === 'soft') s -= 0.3;
  else if (p.feel === 'firm') s += 0.3;

  if (p.pain === 'shoulder') s -= 0.15;
  else if (p.pain === 'back') s += 0.15;

  return Math.min(4.4, Math.max(2.0, s));
}

// Position auf der Skala: jedes Drittel = ein Härtegrad (H2 2,0–2,5 · H3 2,5–3,5 · H4 3,5–4,4),
// damit der Punkt immer im Band des empfohlenen Grads liegt
function gaugePct(s) {
  const v = Math.min(4.4, Math.max(2.0, s));
  let pct;
  if (v < 2.5) pct = (v - 2.0) / 0.5 * 33.3;
  else if (v < 3.5) pct = 33.3 + (v - 2.5) / 1.0 * 33.4;
  else pct = 66.7 + (v - 3.5) / 0.9 * 33.3;
  return Math.round(Math.min(97, Math.max(3, pct)));
}

// shown: der Grad, der tatsächlich empfohlen wird (nur die wählbaren Härtegrade des Betts). Weicht er vom rechnerischen
// ab, nennt die erste Begründung keinen anderen Grad, sondern nur die Richtung.
function evaluate(p, shown) {
  const s = score(p);
  const grade = s < 2.5 ? 'H2' : s < 3.5 ? 'H3' : 'H4';
  const why = [];

  if (shown && shown !== grade) why.push(clampReason(grade, shown, 'du'));
  else if (p.weight <= 70) why.push(`Mit ${p.weight} kg brauchst du keine harte Stütze – ${grade} lässt dich leicht einsinken, ohne dass die Wirbelsäule abknickt.`);
  else if (p.weight <= 100) why.push(`Mit ${p.weight} kg trägt dich ${grade} stabil – kein Durchhängen in der Körpermitte, aber genug Nachgiebigkeit.`);
  else why.push(`Mit ${p.weight} kg braucht deine Matratze Stützkraft – ${grade} hält Becken und Rücken über Jahre in Position.`);

  if (p.position === 'side') why.push('Als Seitenschläfer müssen Schulter und Hüfte einsinken können, damit die Wirbelsäule gerade liegt – das ist eingerechnet.');
  else if (p.position === 'back') why.push('Rückenschläfer brauchen gleichmäßige Stützung, damit die Lendenwirbelsäule nicht ins Hohlkreuz fällt.');
  else if (p.position === 'stomach') why.push('Bauchschläfer: Das Becken darf nicht einsinken – deshalb tendenziell fester.');
  else why.push('Wechselnde Schlafpositionen brauchen einen Härtegrad, der sowohl Seite als auch Rücken gut trägt.');

  if (p.feel === 'soft') why.push('Dein Wunsch nach einem weicheren Liegegefühl ist eingerechnet.');
  else if (p.feel === 'firm') why.push('Dein Wunsch nach einem festeren Liegegefühl ist eingerechnet.');

  if (p.pain === 'shoulder') why.push('Verspannungen in Schulter und Nacken sprechen für etwas mehr Nachgiebigkeit im Schulterbereich.');
  else if (p.pain === 'back') why.push('Beschwerden in Rücken und Hüfte sprechen für mehr Stützkraft in der Körpermitte.');

  const bound = grade === 'H2' ? 2.5 : grade === 'H3' ? (s < 3 ? 2.5 : 3.5) : 3.5;
  const borderline = Math.abs(s - bound) < 0.15 && (!shown || shown === grade);

  return { grade, score: s, pct: gaugePct(s), why, borderline };
}

// Empfohlener Grad ist bei diesem Bett (in dieser Größe/Farbe) nicht wählbar → Richtung nennen, keinen fremden Grad
function clampReason(ideal, shown, who) {
  const firmer = gradeNum(ideal) > gradeNum(shown);
  const need = firmer ? 'mehr Stützkraft' : 'ein weicheres Liegegefühl';
  if (who === 'ihr') return `Eure Profile sprechen eher für ${need} – von den Härtegraden, die ihr hier wählen könnt, passt ${shown} am besten.`;
  return `Dein Profil spricht eher für ${need} – von den Härtegraden, die du hier wählen kannst, passt ${shown} am besten.`;
}
function gradeNum(grade) { return parseInt(String(grade).slice(1), 10) || 0; }

// Gemeinsamer Härtegrad für zwei Personen auf einem Bett mit einem Härtegrad:
// Gleicher Grad → genau dieser (kein Aufschlag – zweimal H3 ergibt nie H4). Verschiedene Grade → Mittel beider Profile,
// leicht Richtung Stützkraft gewichtet (zu weich ist für die schwerere Person das größere Problem); H2 + H4 → H3.
// Abweichung vom Konfigurator: dort hebt der Aufschlag auch zwei gleiche Grade an der Grenze eine Stufe an.
function combined(a, b) {
  const order = ['H2', 'H3', 'H4'];
  const ia = order.indexOf(a.grade), ib = order.indexOf(b.grade);
  let s = (a.score + b.score) / 2 + 0.1;
  let grade = s < 2.5 ? 'H2' : s < 3.5 ? 'H3' : 'H4';
  if (Math.abs(ia - ib) === 2) { grade = 'H3'; s = 3.0; }
  if (a.grade === b.grade) {
    grade = a.grade;
    s = (a.score + b.score) / 2;
  }
  return { grade, score: s, pct: gaugePct(s), borderline: false, differs: a.grade !== b.grade };
}

// Begründung zu zweit – mit den Graden, die angezeigt werden (ga/gb je Person, shown gemeinsam; alle wählbar)
function coupleWhy(ga, gb, shown, ideal) {
  const why = [];
  if (ideal !== shown) why.push(clampReason(ideal, shown, 'ihr'));
  if (ga === gb && ga === shown && ideal === shown) {
    why.push(`Ihr landet beide bei ${shown} – ein Härtegrad fürs ganze Bett passt euch ohne Kompromiss.`);
  } else if (ga !== gb) {
    why.push(`Du: ${ga}, Partner:in: ${gb}. Das Bett hat einen Härtegrad für beide Seiten – deshalb mitteln wir beide Profile und gewichten die Stützkraft für die schwerere Person etwas höher.`);
    why.push('Zu weich wäre für die schwerere Person ein Problem (Durchhängen), etwas fester ist für die leichtere Person gut verträglich.');
  }
  return why;
}

// ---- Dialog -----------------------------------------------------------------------------------------------------

const ICON_CHECK = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960" fill="currentColor" width="16" height="16" aria-hidden="true"><path d="M382-240 154-468l57-57 171 171 367-367 57 57-424 424Z"/></svg>';
const ICON_CLOSE = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960" fill="currentColor" width="18" height="18" aria-hidden="true"><path d="M256-200l-56-56 224-224-224-224 56-56 224 224 224-224 56 56-224 224 224 224-56 56-224-224-224 224Z"/></svg>';

const esc = (value) => String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

const state = {
  mode: 'single', // 'single' | 'couple'
  person: 0,
  phase: 'form', // 'form' | 'result'
  persons: [
    { height: 178, weight: 80, position: null, feel: 'balanced', pain: 'none' },
    { height: 168, weight: 65, position: null, feel: 'balanced', pain: 'none' },
  ],
  result: null,
  choice: null,
  opener: null,
  fieldset: null,
};

let dialog = null;
let sheet = null;

const q = (sel) => dialog.querySelector(sel);
const qa = (sel) => Array.from(dialog.querySelectorAll(sel));
const current = () => state.persons[state.person];
const personCount = () => (state.mode === 'couple' ? 2 : 1);
const canCalculate = () => state.persons.slice(0, personCount()).every((p) => !!p.position);

function chipButtons(list, attr) {
  return list.map((o) => `<button type="button" class="hgr__chip" ${attr}="${o.key}" aria-pressed="false">${esc(o.label)}<small>${esc(o.hint)}</small></button>`).join('');
}

function ensureDialog() {
  if (dialog) return;
  dialog = document.createElement('dialog');
  dialog.className = 'hgr';
  dialog.setAttribute('aria-labelledby', 'hgr-title');
  dialog.innerHTML = `
    <div class="hgr__sheet" tabindex="-1" autofocus>
      <div class="hgr__grip" aria-hidden="true"></div>
      <div class="hgr__head">
        <div>
          <p class="hgr__kicker">Härtegrad-Rechner</p>
          <p class="hgr__title" id="hgr-title" data-hgr-title></p>
          <p class="hgr__sub" data-hgr-sub></p>
        </div>
        <button type="button" class="hgr__x" data-hgr-close aria-label="Schließen">${ICON_CLOSE}</button>
      </div>

      <div data-hgr-form>
        <div class="hgr__seg" role="group" aria-label="Wer schläft im Bett?">
          <button type="button" class="hgr__seg-btn" data-hgr-mode="single" aria-pressed="true"><b>Allein</b><small>nur dein Profil</small></button>
          <button type="button" class="hgr__seg-btn" data-hgr-mode="couple" aria-pressed="false"><b>Zu zweit</b><small>ein Härtegrad für euch beide</small></button>
        </div>
        <div class="hgr__tabs" data-hgr-tabs hidden>
          <button type="button" class="hgr__tab" data-hgr-person="0">Du<i hidden></i></button>
          <button type="button" class="hgr__tab" data-hgr-person="1">Partner:in<i hidden></i></button>
        </div>

        <div class="hgr__sliders">
          <div class="hgr__field">
            <label class="hgr__label" for="hgr-weight"><span>Körpergewicht</span><span class="hgr__val" data-hgr-val="weight"></span></label>
            <input class="hgr__range" id="hgr-weight" type="range" min="40" max="160" step="1" data-hgr-input="weight">
            <div class="hgr__minmax" aria-hidden="true"><span>40 kg</span><span>160 kg</span></div>
          </div>
          <div class="hgr__field">
            <label class="hgr__label" for="hgr-height"><span>Körpergröße</span><span class="hgr__val" data-hgr-val="height"></span></label>
            <input class="hgr__range" id="hgr-height" type="range" min="140" max="210" step="1" data-hgr-input="height">
            <div class="hgr__minmax" aria-hidden="true"><span>140 cm</span><span>210 cm</span></div>
          </div>
        </div>

        <div class="hgr__field">
          <p class="hgr__label" data-hgr-text="position"></p>
          <div class="hgr__chips hgr__chips--4">${chipButtons(POSITIONS, 'data-hgr-pos')}</div>
        </div>
        <div class="hgr__field">
          <p class="hgr__label" data-hgr-text="feel"></p>
          <div class="hgr__chips hgr__chips--3">${chipButtons(FEELS, 'data-hgr-feel')}</div>
        </div>

        <button type="button" class="hgr__cta" data-hgr-next></button>
        <p class="hgr__hint" data-hgr-hint hidden></p>
      </div>

      <div data-hgr-result hidden></div>
    </div>`;
  document.body.appendChild(dialog);
  sheet = q('.hgr__sheet');

  dialog.addEventListener('click', onClick);
  dialog.addEventListener('input', (event) => {
    const input = event.target.closest('[data-hgr-input]');
    if (!input) return;
    current()[input.getAttribute('data-hgr-input')] = parseInt(input.value, 10);
    sync();
  });
  dialog.addEventListener('close', cleanup);
  // Ohne natives showModal (sehr alte Browser): Escape selbst abfangen
  dialog.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && dialog.classList.contains('hgr--fallback')) close();
  });
}

function onClick(event) {
  // Klick auf den abgedunkelten Hintergrund (außerhalb des Blatts) schließt
  if (event.target === dialog) { close(); return; }
  const el = event.target.closest('button');
  if (!el || !dialog.contains(el)) return;

  if (el.hasAttribute('data-hgr-close')) { close(); return; }
  if (el.hasAttribute('data-hgr-mode')) {
    state.mode = el.getAttribute('data-hgr-mode');
    state.person = 0;
  } else if (el.hasAttribute('data-hgr-person')) {
    state.person = parseInt(el.getAttribute('data-hgr-person'), 10) || 0;
  } else if (el.hasAttribute('data-hgr-pos')) {
    current().position = el.getAttribute('data-hgr-pos');
  } else if (el.hasAttribute('data-hgr-feel')) {
    current().feel = el.getAttribute('data-hgr-feel');
  } else if (el.hasAttribute('data-hgr-next')) {
    if (state.mode === 'couple' && state.person === 0) {
      state.person = 1;
      sync();
      const tabs = q('[data-hgr-tabs]');
      sheet.scrollTo({ top: Math.max(0, tabs.offsetTop - 12), behavior: 'smooth' });
      return;
    }
    calculate();
    return;
  } else if (el.hasAttribute('data-hgr-apply')) {
    apply();
    return;
  } else if (el.hasAttribute('data-hgr-back')) {
    state.phase = 'form';
    state.person = 0;
    sync();
    sheet.scrollTop = 0;
    return;
  } else {
    return;
  }
  sync();
}

function sync() {
  const couple = state.mode === 'couple';
  const p = current();
  const inForm = state.phase === 'form';

  q('[data-hgr-form]').hidden = !inForm;
  q('[data-hgr-result]').hidden = inForm;
  q('[data-hgr-title]').textContent = inForm ? (couple ? 'Wie schlaft ihr?' : 'Wie schläfst du?') : (couple ? 'Euer Ergebnis' : 'Dein Ergebnis');
  const sub = q('[data-hgr-sub]');
  sub.hidden = !inForm;
  sub.textContent = couple
    ? 'Das Bett hat einen Härtegrad für beide Seiten. Füllt beide Profile aus – wir finden den besten gemeinsamen.'
    : 'Vier Angaben, dann kennst du deinen Härtegrad.';

  qa('[data-hgr-mode]').forEach((b) => {
    const on = b.getAttribute('data-hgr-mode') === state.mode;
    b.classList.toggle('is-on', on);
    b.setAttribute('aria-pressed', on ? 'true' : 'false');
  });
  q('[data-hgr-tabs]').hidden = !couple;
  qa('[data-hgr-person]').forEach((b) => {
    const i = parseInt(b.getAttribute('data-hgr-person'), 10);
    b.classList.toggle('is-on', i === state.person);
    b.setAttribute('aria-pressed', i === state.person ? 'true' : 'false');
    b.querySelector('i').hidden = !state.persons[i].position;
  });

  ['weight', 'height'].forEach((key) => {
    const input = q(`[data-hgr-input="${key}"]`);
    const text = p[key] + (key === 'weight' ? ' kg' : ' cm');
    if (String(input.value) !== String(p[key])) input.value = p[key];
    input.setAttribute('aria-valuetext', text);
    q(`[data-hgr-val="${key}"]`).textContent = text;
  });
  qa('[data-hgr-pos]').forEach((b) => {
    const on = b.getAttribute('data-hgr-pos') === p.position;
    b.classList.toggle('is-on', on);
    b.setAttribute('aria-pressed', on ? 'true' : 'false');
  });
  qa('[data-hgr-feel]').forEach((b) => {
    const on = b.getAttribute('data-hgr-feel') === p.feel;
    b.classList.toggle('is-on', on);
    b.setAttribute('aria-pressed', on ? 'true' : 'false');
  });

  const partner = couple && state.person === 1;
  q('[data-hgr-text="position"]').textContent = partner ? 'Partner:in schläft meistens …' : 'Du schläfst meistens …';
  q('[data-hgr-text="feel"]').textContent = partner ? 'Liegegefühl von Partner:in' : 'Dein Liegegefühl';

  const next = q('[data-hgr-next]');
  const toPartner = couple && state.person === 0;
  next.textContent = toPartner ? 'Weiter zu Partner:in' : 'Härtegrad berechnen';
  next.disabled = toPartner ? !state.persons[0].position : !canCalculate();

  let hint = '';
  if (!p.position) hint = 'Bitte Schlafposition wählen.';
  else if (partner && !state.persons[0].position) hint = 'Bei „Du“ fehlt noch die Schlafposition.';
  const hintEl = q('[data-hgr-hint]');
  hintEl.hidden = !hint;
  hintEl.textContent = hint;
}

// ---- Härtegrad-Werte der Variantenauswahl ------------------------------------------------------------------------

// Alle Härtegrad-Werte einer Option (fieldset): Grad (H2/H3/H4 …), Wert, Element und ob er gerade wählbar ist – das Theme
// blendet Werte ohne Kombination in dieser Größe/Farbe per hidden aus und markiert ausverkaufte mit is-disabled
// (Lager-Modus: nur Lagerware wählbar).
function choicesIn(fieldset) {
  if (!fieldset) return [];
  return Array.from(fieldset.querySelectorAll('[data-option-value]')).map((el) => {
    let input = null;
    let value;
    if (el.tagName === 'LABEL' && el.htmlFor) {
      input = document.getElementById(el.htmlFor);
      value = input ? input.value : el.textContent;
    } else {
      value = el.getAttribute('value') || el.textContent;
    }
    value = String(value || '').trim();
    const match = value.toUpperCase().replace(/\s+/g, '').match(/^H(\d)/);
    if (!match) return null;
    const offered = !el.hidden && !el.classList.contains('is-disabled') && !(input && input.disabled);
    return { grade: `H${match[1]}`, value, el, input, offered };
  }).filter(Boolean);
}
function choices() { return choicesIn(state.fieldset); }
function offeredGrades(fieldset) {
  return Array.from(new Set(choicesIn(fieldset).filter((c) => c.offered).map((c) => c.grade)));
}

// Empfohlener Grad, wenn wählbar – sonst der nächstliegende wählbare. Liegen zwei gleich weit weg (z. B. H3 gesucht,
// nur H2 und H4 da), entscheidet der Score: obere Hälfte des Bands → fester, untere → weicher.
const BAND_MID = { H1: 1.75, H2: 2.25, H3: 3.0, H4: 3.95, H5: 4.6 };
function pickChoice(grade, scoreValue) {
  const offered = choices().filter((c) => c.offered);
  if (!offered.length) return null;
  const target = gradeNum(grade);
  const preferFirm = typeof scoreValue === 'number' && scoreValue >= (BAND_MID[grade] || 3);
  return offered.slice().sort((a, b) => {
    const d = Math.abs(gradeNum(a.grade) - target) - Math.abs(gradeNum(b.grade) - target);
    if (d) return d;
    return preferFirm ? gradeNum(b.grade) - gradeNum(a.grade) : gradeNum(a.grade) - gradeNum(b.grade);
  })[0];
}

// Punkt auf der Skala: im Band des angezeigten Grads – wurde auf einen wählbaren Grad ausgewichen, an dessen Rand
// zur rechnerischen Empfehlung hin
const BAND_PCT = { H2: [3, 33.3], H3: [33.3, 66.7], H4: [66.7, 97] };
function shownPct(ideal, shown, scoreValue) {
  if (ideal === shown || !BAND_PCT[shown]) return gaugePct(scoreValue);
  const [lo, hi] = BAND_PCT[shown];
  return Math.round(gradeNum(ideal) > gradeNum(shown) ? hi - 4 : lo + 4);
}

function calculate() {
  if (!canCalculate()) return;
  const couple = state.mode === 'couple';
  let res;
  if (couple) {
    const a = evaluate(state.persons[0]);
    const b = evaluate(state.persons[1]);
    const c = combined(a, b);
    const choice = pickChoice(c.grade, c.score);
    const shown = choice ? choice.grade : c.grade;
    // je Person der beste wählbare Grad – so taucht im Ergebnis kein Grad auf, den es bei diesem Bett nicht gibt
    const ga = (pickChoice(a.grade, a.score) || a).grade;
    const gb = (pickChoice(b.grade, b.score) || b).grade;
    res = { ideal: c.grade, grade: shown, choice, pct: shownPct(c.grade, shown, c.score), why: coupleWhy(ga, gb, shown, c.grade), borderline: false, who: 'Euer gemeinsamer Härtegrad', pair: `Du: ${ga} · Partner:in: ${gb}` };
  } else {
    const ideal = evaluate(state.persons[0]);
    const choice = pickChoice(ideal.grade, ideal.score);
    const shown = choice ? choice.grade : ideal.grade;
    const r = evaluate(state.persons[0], shown);
    res = { ideal: ideal.grade, grade: shown, choice, pct: shownPct(ideal.grade, shown, ideal.score), why: r.why, borderline: r.borderline, who: 'Dein Härtegrad', pair: '' };
  }
  state.result = res;
  state.choice = res.choice;
  renderResult();
  state.phase = 'result';
  sync();
  sheet.scrollTop = 0;
}

function renderResult() {
  const res = state.result;
  const choice = state.choice;
  const all = choices();
  const offered = new Set(all.filter((c) => c.offered).map((c) => c.grade));
  // Lager-Modus (snippets/lager-mode.liquid, „Nur auf Lager“ aktiv): die rechnerische Empfehlung gibt es bei diesem Bett,
  // nur nicht sofort lieferbar → darauf hinweisen, dass sie unter „Alle Varianten“ bestellbar ist
  const stockOnly = !!window.__lgFetchPatched && !window.__lgShowAll && !!document.querySelector('[data-lgm-toggle]');
  let note = '';
  if (!choice) note = 'Den Härtegrad wählst du direkt in der Auswahl auf der Produktseite.';
  else if (res.ideal !== res.grade && stockOnly && all.some((c) => c.grade === res.ideal)) note = `${res.ideal} gibt es bei diesem Bett auch – nur nicht sofort lieferbar. Unter „Alle Varianten“ kannst du ${res.ideal} bestellen.`;

  q('[data-hgr-result]').innerHTML = `
    <div class="hgr__res">
      <p class="hgr__who">${esc(res.who)}</p>
      <div class="hgr__grade-row"><span class="hgr__grade">${esc(res.grade)}</span><span class="hgr__name">${esc(GRADE_NAMES[res.grade] || '')}</span></div>
      ${res.pair ? `<p class="hgr__pair">${esc(res.pair)}</p>` : ''}
      <div class="hgr__gauge" aria-hidden="true"><i style="left:${res.pct}%"></i></div>
      <div class="hgr__gauge-l" aria-hidden="true">${['H2', 'H3', 'H4'].map((g) => `<span${offered.size && !offered.has(g) ? ' class="is-off"' : ''}>${g}</span>`).join('')}</div>
      <ul class="hgr__why">${res.why.map((w) => `<li>${esc(w)}</li>`).join('')}</ul>
    </div>
    ${res.borderline ? '<p class="hgr__note">Du liegst genau an der Grenze. Faustregel: Wer lieber einsinkt, nimmt den weicheren Grad.</p>' : ''}
    ${note ? `<p class="hgr__note">${esc(note)}</p>` : ''}
    ${choice ? `<button type="button" class="hgr__cta" data-hgr-apply>${esc(choice.grade)} übernehmen</button>` : ''}
    <button type="button" class="hgr__ghost" data-hgr-back>Angaben anpassen</button>`;
}

// Link nur zeigen, wenn gerade mindestens zwei verschiedene Härtegrade wählbar sind (Größe/Farbe/Lager-Modus ändern das)
function refreshLinks() {
  document.querySelectorAll('[data-hgr-open]').forEach((link) => {
    link.hidden = offeredGrades(link.closest('fieldset')).length < 2;
  });
}

// ---- Übernehmen ----------------------------------------------------------------------------------------------------

function apply() {
  const choice = state.choice;
  if (!choice) return;
  if (choice.input) {
    // Radio wie ein Klick setzen; das change-Ereignis erreicht [data-option-selector] → variant-picker wählt die Variante
    if (!choice.input.checked) {
      choice.input.checked = true;
      choice.input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  } else {
    choice.el.click(); // Dropdown-Darstellung: Option der Liste anklicken
  }
  showDone(choice);
  close();
}

// Kleine Bestätigung unter der Härtegrad-Auswahl; verschwindet, sobald jemand dort von Hand etwas anderes wählt
function showDone(choice) {
  const fieldset = state.fieldset;
  if (!fieldset) return;
  let note = fieldset.querySelector('[data-hgr-done]');
  if (!note) {
    note = document.createElement('p');
    note.className = 'hgr-done';
    note.setAttribute('data-hgr-done', '');
    note.setAttribute('role', 'status');
    fieldset.appendChild(note);
  }
  const name = GRADE_NAMES[choice.grade] ? ` · ${GRADE_NAMES[choice.grade]}` : '';
  const extra = state.mode === 'couple' ? ' – euer gemeinsamer Härtegrad' : '';
  note.innerHTML = `${ICON_CHECK}<span>Empfehlung übernommen: <strong>${esc(choice.grade + name)}</strong>${esc(extra)}</span>`;
  note.setAttribute('data-value', choice.value);
  if (!fieldset.hasAttribute('data-hgr-watch')) {
    fieldset.setAttribute('data-hgr-watch', '');
    fieldset.addEventListener('change', () => {
      const done = fieldset.querySelector('[data-hgr-done]');
      const checked = fieldset.querySelector('input[name^="option"]:checked, input[name^="option"][type="hidden"]');
      if (done && checked && checked.value !== done.getAttribute('data-value')) done.remove();
    });
  }
}

// ---- Öffnen / Schließen ----------------------------------------------------------------------------------------------

function open(opener) {
  ensureDialog();
  state.opener = opener;
  state.fieldset = opener.closest('fieldset');
  state.phase = 'form';
  state.person = 0;
  sync();
  document.documentElement.classList.add('hgr-lock');
  if (typeof dialog.showModal === 'function') {
    try { dialog.showModal(); } catch (error) { dialog.setAttribute('open', ''); }
  } else {
    dialog.classList.add('hgr--fallback');
    dialog.setAttribute('open', '');
  }
  sheet.scrollTop = 0;
}

function close() {
  if (!dialog) return;
  if (dialog.open && typeof dialog.close === 'function') dialog.close(); // löst „close“ → cleanup aus
  else { dialog.removeAttribute('open'); cleanup(); }
}

// Fokus gibt das native <dialog> beim Schließen selbst an den Link zurück (nur wenn er vorher fokussiert war) –
// kein eigenes focus(), sonst zeigt der Browser nach Tippen/Klicken einen Fokusrahmen um den Link.
function cleanup() {
  document.documentElement.classList.remove('hgr-lock');
}

// ---- Start --------------------------------------------------------------------------------------------------------

(function init() {
  if (window.__hgrReady) return;
  const first = document.querySelector('[data-hgr-open]');
  if (!first) return;
  window.__hgrReady = true;
  const cssUrl = first.getAttribute('data-hgr-css');
  if (cssUrl && !document.querySelector('link[data-hgr-css]')) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = cssUrl;
    link.setAttribute('data-hgr-css', '');
    document.head.appendChild(link);
  }
  // Delegiert: funktioniert auch, wenn das Theme die Variantenauswahl neu aufbaut
  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-lgm-toggle]')) setTimeout(refreshLinks, 400); // Lager-Modus: „Nur auf Lager“ / „Alle Varianten“
    const opener = event.target.closest('[data-hgr-open]');
    if (!opener) return;
    event.preventDefault();
    open(opener);
  });
  // variant:change feuert das Theme nach dem Aus-/Ausgrauen der Werte (bubbles bis document)
  document.addEventListener('variant:change', () => refreshLinks());
  refreshLinks();
  // falls das Theme die Werte erst nach diesem Skript aus-/ausgraut
  window.addEventListener('load', refreshLinks);
  setTimeout(refreshLinks, 1500);
})();
